import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L, { LatLngTuple } from 'leaflet';
import { ResponseDataGet } from '../../../../models/responseDataGet';
import { EntTerritorialsUnits } from '../../../../models/handBooks/territorialsUnits';
import 'leaflet/dist/leaflet.css';
import styles from './styles_map.module.css';
import LeasingSelectYear from '../../../mechanization/leasing/Leasingselect';
import { Select } from 'antd';
import { IFertilizerOctoberResponse, IFertilizerOctoberItem } from '../api/types';

interface IDistrict {
  territorialUnitDistrict: string;
  necessary: number;
  brought: number;
  winterCerealFertilizerPercentage: number;
}

interface IRegion {
  territorialUnitRegion: string;
  districts: IDistrict[];
}

interface FertilizationCerealsMapProps {
  latitude: number;
  longitude: number;
  polygonsData?: ResponseDataGet<EntTerritorialsUnits>;
  selectedYear: number | null;
  onYearChange: (value: number | null) => void;
  isLoadingFertilizers: boolean;
  isLoadingFertilizersOnMap: boolean;
  onRegionClick: (regionId: number, regionName: string) => void;
  onResetRegion: () => void;
  selectedRegionName: string;
  fertilizerData?: IFertilizerOctoberResponse;
  initialRegionId?: number;
}

const MapComponent: React.FC<{ 
  polygonsData?: ResponseDataGet<EntTerritorialsUnits>;
  onRegionClick: (regionId: number, regionName: string) => void;
  initialRegionId?: number;
}> = ({ polygonsData, onRegionClick, initialRegionId }) => {
  const map = useMap();
  const polygonLayersRef = useRef<{[key: number]: L.Polygon}>({});
  const activePolygonRef = useRef<L.Polygon | null>(null);

  const setActivePolygonStyle = useCallback((polygonLayer: L.Polygon) => {
    if (activePolygonRef.current !== polygonLayer) {
      Object.values(polygonLayersRef.current).forEach(layer => {
        layer.setStyle({
          fillOpacity: 0.1,
          weight: 1,
          color: 'rgba(77, 142, 33, 1)',
          fillColor: 'rgba(77, 142, 33, 1)'
        });
      });
      polygonLayer.setStyle({
        fillOpacity: 0.3,
        weight: 2,
        color: 'green',
        fillColor: 'green'
      });
      activePolygonRef.current = polygonLayer;
    }
  }, []);

  useEffect(() => {
    if (!map || !polygonsData?.res) return;

    const polygonsArray = Array.isArray(polygonsData.res) ? polygonsData.res : [polygonsData.res];

    Object.values(polygonLayersRef.current).forEach(layer => layer.remove());
    polygonLayersRef.current = {};

    polygonsArray.forEach((polygon: EntTerritorialsUnits) => {
      if (!polygon.id || !polygon.nameRu || !polygon.polygonJson) return;

      try {
        const parsedPolygon = JSON.parse(polygon.polygonJson);
        if (!parsedPolygon || !Array.isArray(parsedPolygon) || !parsedPolygon[0]) return;

        const geoJson = parsedPolygon[0].geojson;
        if (!geoJson || !geoJson.coordinates || !Array.isArray(geoJson.coordinates)) return;

        const coordinates = geoJson.type === 'MultiPolygon' 
          ? geoJson.coordinates[0][0]  
          : geoJson.coordinates[0];    

        if (!Array.isArray(coordinates)) return;

        const latLngs: [number, number][] = coordinates.map((coord: number[]) => [coord[1], coord[0]]);

        const polygonLayer = L.polygon(latLngs, {
          color: 'rgba(77, 142, 33, 1)',
          fillColor: 'rgba(77, 142, 33, 1)',
          fillOpacity: 0.1,
          weight: 1
        }).addTo(map);

        polygonLayer.on('click', () => {
          if (polygon.id) {
            setActivePolygonStyle(polygonLayer);
            onRegionClick(polygon.id, polygon.nameRu!);
          }
        });

        if (polygon.id != null) {
          polygonLayersRef.current[polygon.id] = polygonLayer;
          if (initialRegionId && polygon.id === initialRegionId) {
            setActivePolygonStyle(polygonLayer);
          }
        }
      } catch (error) {
      
      }
    });

    if (Object.values(polygonLayersRef.current).length > 0) {
      const group = L.featureGroup(Object.values(polygonLayersRef.current));
      map.fitBounds(group.getBounds());
    }
  }, [map, polygonsData, onRegionClick, setActivePolygonStyle, initialRegionId]);

  return null;
};

export const FertilizationCerealsMap: React.FC<FertilizationCerealsMapProps> = ({ 
  latitude, 
  longitude, 
  polygonsData,
  selectedYear,
  onYearChange,
  isLoadingFertilizers,
  isLoadingFertilizersOnMap,
  onRegionClick,
  onResetRegion,
  selectedRegionName,
  fertilizerData,
  initialRegionId
}) => {
  const center: LatLngTuple = [latitude, longitude];
  const [searchText, setSearchText] = useState('');

  const getDistrictOptions = () => {
    if (!fertilizerData?.res) return [];

    type District = {
      territorialUnitDistrict: string;
      necessary: number;
      brought: number;
      winterCerealFertilizerPercentage: number;
    };

    let districts: District[] = [];
    if (selectedRegionName) {
      const currentRegionData = fertilizerData.res.find(
        (region: IFertilizerOctoberItem) => region.territorialUnitRegion === selectedRegionName
      );
      if (currentRegionData) {
        districts = currentRegionData.districts;
      }
    } else {
      districts = fertilizerData.res.flatMap(region => region.districts);
    }

    return districts.map(district => ({
      value: district.territorialUnitDistrict,
      label: district.territorialUnitDistrict
    }));
  };

  const renderTableData = () => {
    if (!fertilizerData?.res) return null;

    type District = {
      territorialUnitDistrict: string;
      necessary: number;
      brought: number;
      winterCerealFertilizerPercentage: number;
    };

    let districts: District[] = [];
    
    if (selectedRegionName) {
      const currentRegionData = fertilizerData.res.find(
        (region: IFertilizerOctoberItem) => region.territorialUnitRegion === selectedRegionName
      );
      
      if (currentRegionData) {
        districts = currentRegionData.districts;
      }
    } else {
      districts = fertilizerData.res.flatMap(region => region.districts);
    }

    const searchValue = searchText?.toLowerCase() || '';
    const filteredDistricts = districts.filter(district =>
      district.territorialUnitDistrict.toLowerCase().includes(searchValue)
    );

    if (selectedRegionName && filteredDistricts.length === 0) {
      return (
        <div className={styles.noData}>
          Нет данных
        </div>
      );
    }


    return (
      <div className={styles.statsInfo}>
        {filteredDistricts.map((district, index) => (
          <div key={index} className={styles.statsSection}>
            <div className={styles.cropInfo}>
              <span className={styles.cropName}>{district.territorialUnitDistrict}</span>
              <span className={styles.percentage}>{(district.winterCerealFertilizerPercentage).toFixed(1)}%</span>
            </div>
            <div className={styles.areaInfo}>
              <span className={styles.areaLabel}>Необходимо:</span>
              <span className={styles.areaValue}>{district.necessary}</span>
            </div>
            <div className={styles.areaInfo}>
              <span className={styles.areaLabel}>Внесено:</span>
              <span className={styles.areaValue}>{district.brought}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.mapContainer}>
        <MapContainer
         center={[latitude, longitude]}
         zoom={6}
         minZoom={6}
         maxZoom={6}
         dragging={false}
         scrollWheelZoom={false}
         doubleClickZoom={false}
         touchZoom={false}
         style={{ height: '100%', width: '100%' }}
         attributionControl={false}
         zoomControl={false}
        >
    <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
        
          <MapComponent 
            polygonsData={polygonsData}
            onRegionClick={onRegionClick}
            initialRegionId={initialRegionId}
          />
        </MapContainer>
        <div className={styles.yearSelector}>
          <LeasingSelectYear
            value={selectedYear}
            onChange={onYearChange}
          />
        </div>
      </div>
      <div className={styles.sidebar}>
        <div className={styles.statsContainer}>
          <div className={styles.searchContainer}>
            <div className={styles.headerContainer}>
              <h3 className={styles.headerTitle}>
                {selectedRegionName ? ` ${selectedRegionName}` : 'Данные по республике'}
              </h3>
              {selectedRegionName && (
                <button 
                  className={styles.resetButton}
                  onClick={onResetRegion}
                  title="Показать данные по всей республике"
                >
                  ×
                </button>
              )}
            </div>
            <Select
              showSearch
              allowClear
              placeholder="Поиск района"
              value={searchText || undefined}
              onChange={setSearchText}
              options={getDistrictOptions()}
              className={styles.searchSelect}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
          {isLoadingFertilizers ? (
            <div className={styles.loading}>Загрузка...</div>
          ) : (
            renderTableData()
          )}
        </div>
      </div>
    </div>
  );
};
